'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PropsWithClassname } from '@/app/types';
import { ProjectPhoto } from '@/app/api';
import { isImageLandscapeOriented } from '@/app/utils/image';

import PlayIcon from '@/app/components/PlayIcon/PlayIcon';

import MediaDisplay, {
  ImageDisplayProps,
  MediaDisplayProps,
  MediaType,
  VideoDisplayProps,
} from '@/app/components/MediaDisplay/MediaDisplay';
import { EndsAtWidget } from '@/app/components/ProjectPageHeaderForAbTest/components/ImageCarousell';
import { useIsUserInNewHeaderLayoutGroup } from '@/app/hooks/useIsUserInNewHeaderLayoutGroup';
import { useProjectData } from '@/app/providers/ProjectProvider';

export type ImageSwiperCoverImage = {
  small: string;
  large: string;
};

interface ImageSwiperProps extends PropsWithClassname {
  images: ProjectPhoto[];
  coverImage: ImageSwiperCoverImage;
  alt?: string;
  mainVideoSlug?: string;
}

const SIZES = '100vw, (min-width: 640px) 790px, (min-width: 1024px) 640px';

export const getCoverVideoMedia = (mainVideoSlug: string, alt?: string): VideoDisplayProps => ({
  type: MediaType.VIDEO,
  videoUrl: `https://www.youtube.com/embed/${mainVideoSlug}?controls=1&enablejsapi=1`,
  imageUrl: `https://img.youtube.com/vi/${mainVideoSlug}/hqdefault.jpg`,
  sizes: SIZES,
  imageAlt: alt,
});

const getCoverImageMedia = (src: string, alt?: string): ImageDisplayProps => ({
  type: MediaType.IMAGE,
  largeImageSrc: src,
  smallImageSrc: src,
  sizes: SIZES,
  alt,
  isLandscapeOriented: true,
});

const ImageSwiper = ({ images, alt, mainVideoSlug, className, coverImage }: ImageSwiperProps) => {
  const isUserInNewHeaderLayoutGroup = useIsUserInNewHeaderLayoutGroup();
  const { project } = useProjectData();
  const scheduledEnding = project.timestamps.scheduledEnding;

  const mediaInSwiper: (ImageDisplayProps | VideoDisplayProps)[] = useMemo(() => {
    const base: ImageDisplayProps[] = images.map((image) => ({
      type: MediaType.IMAGE,
      largeImageSrc: isImageLandscapeOriented(image) ? image.sizes.smallCropped.path : image.sizes.small.path,
      smallImageSrc: image.sizes.miniCropped.path,
      sizes: SIZES,
      alt: image.description ?? alt,
      isLandscapeOriented: isImageLandscapeOriented(image),
    }));

    if (!mainVideoSlug) {
      return base;
    }

    return [getCoverVideoMedia(mainVideoSlug, alt), ...base];
  }, [images, mainVideoSlug, alt]);

  const coverMedia: MediaDisplayProps = useMemo(() => {
    return mainVideoSlug ? getCoverVideoMedia(mainVideoSlug, alt) : getCoverImageMedia(coverImage.large, alt);
  }, [coverImage, mainVideoSlug]);

  const [selectedImage, setSelectedImage] = useState<MediaDisplayProps>();
  const [spaceLeftToScrollDown, setSpaceLeftToScrollDown] = useState(0);
  const [spaceLeftToScrollUp, setSpaceLeftToScrollUp] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleArrowDownClick = useCallback(() => {
    if (!scrollContainerRef.current) {
      return null;
    }

    const containerHeight = scrollContainerRef.current.getBoundingClientRect().height;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const scrollTop = scrollContainerRef.current.scrollTop;

    scrollContainerRef.current.scroll({
      top: scrollTop + containerHeight,
      behavior: 'smooth',
    });

    setSpaceLeftToScrollUp(Math.max(scrollTop + containerHeight, scrollHeight - containerHeight));
    setSpaceLeftToScrollDown(Math.max(scrollHeight - (containerHeight * 2 + scrollTop), 0));
  }, [scrollContainerRef, setSpaceLeftToScrollDown, setSpaceLeftToScrollUp]);

  const handleArrowUpClick = useCallback(() => {
    if (!scrollContainerRef.current) {
      return null;
    }

    const containerHeight = scrollContainerRef.current.getBoundingClientRect().height;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const scrollTop = scrollContainerRef.current.scrollTop;

    scrollContainerRef.current.scroll({
      top: scrollTop - containerHeight,
      behavior: 'smooth',
    });

    setSpaceLeftToScrollUp(Math.max(scrollTop - containerHeight, 0));
    setSpaceLeftToScrollDown(Math.max(scrollHeight - containerHeight, scrollTop - containerHeight));
  }, [scrollContainerRef, setSpaceLeftToScrollDown]);

  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    const containerHeight = scrollContainerRef.current.getBoundingClientRect().height;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const scrollTop = scrollContainerRef.current.scrollTop;

    setSpaceLeftToScrollDown(scrollHeight - (containerHeight + scrollTop));
    setSpaceLeftToScrollUp(scrollTop);
  }, []);

  return (
    <div
      className={classNames(
        className,
        'lg:mb-[10px] flex flex-1 flex-col lg:flex-row items-stretch w-full lg:max-w-[744px] lg:max-h-[370px] relative',
      )}
    >
      <div className="mt-[16px] mb-[10px] lg:w-full lg:max-w-[592px] lg:mt-0 lg:mb-0 relative">
        {isUserInNewHeaderLayoutGroup && scheduledEnding && (
          <EndsAtWidget absolute endsAt={scheduledEnding} className="md:top-0 md:bottom-auto md:-translate-y-1/2" />
        )}
        <figure className="bg-primary-light rounded-[4px] relative pb-[62.5%]">
          <div className="m-0 rounded-md overflow-hidden">
            {!!selectedImage ? <MediaDisplay {...selectedImage} /> : <MediaDisplay {...coverMedia} />}
          </div>
        </figure>
      </div>
      <div className="lg:min-w-[17.4%] my-0 mx-[-24px] lg:mr-0 lg:ml-[16px] lg:relative">
        <button
          className={classNames(
            `absolute top-0 left-[2px] right-[2px] hidden bg-neutral-semiTransparent h-[28px] bg-[url('/public/images/arrow-down.svg')] bg-no-repeat bg-[10px 12px] bg-center z-10 rotate-180`,
            {
              'lg:block': spaceLeftToScrollUp && spaceLeftToScrollUp > 0,
            },
          )}
          onClick={handleArrowUpClick}
        />
        <button
          className={classNames(
            `absolute bottom-0 left-[2px] right-[2px] hidden bg-neutral-semiTransparent h-[28px] bg-[url('/public/images/arrow-down.svg')] bg-no-repeat bg-[10px 12px] bg-center z-10`,
            {
              'lg:block': spaceLeftToScrollDown && spaceLeftToScrollDown > 0,
            },
          )}
          onClick={handleArrowDownClick}
        />

        <div
          ref={scrollContainerRef}
          className="flex flex-row lg:flex-col lg:absolute top-0 bottom-0 left-0 right-0 lg:overflow-hidden overflow-x-auto px-[24px] lg:px-0 pb-[4px] lg:pb-0"
        >
          {mediaInSwiper.map((media) => (
            <button
              key={media.type === MediaType.IMAGE ? media.largeImageSrc : media.imageUrl}
              className="w-[30%] lg:w-full ml-[8px] first:ml-0 lg:ml-0 lg:mb-[13%] overflow-hidden shrink-0"
            >
              <span className="block relative overflow-hidden w-full pb-[62.5%]">
                <>
                  <img
                    src={media.type === MediaType.IMAGE ? media.smallImageSrc : media.imageUrl}
                    alt={alt ?? ' - '}
                    className={classNames(
                      'absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] max-w-full max-h-full object-cover rounded-[4px] border-[2px] overflow-hidden transition-all',
                      {
                        'border-primary-light': media === selectedImage,
                        'border-secondary-light': media !== selectedImage,
                      },
                    )}
                    onClick={() => setSelectedImage(media)}
                    sizes="180px, (min-width: 1024px) 130px"
                  />
                  {media.type === MediaType.VIDEO && (
                    <PlayIcon
                      onClick={() => setSelectedImage(media)}
                      className="text-primary-mid absolute top-1/2 left-1/2 translate-x-[-50%] scale-[.5] translate-y-[-50%] cursor-pointer"
                    />
                  )}
                </>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImageSwiper;
